import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Mirror Phase 1 range tabs for visual consistency.
const RANGES = [
  { v: "today", l: "Today" },
  { v: "7d",    l: "7 Days" },
  { v: "30d",   l: "30 Days" },
  { v: "90d",   l: "90 Days" },
  { v: "all",   l: "All Time" },
];

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

export default function CustomerIntentAnalytics() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/customer-intent?range=${range}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load customer intent analytics"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range]);

  const rows = data?.rows || [];
  const capturePct = data && data.total_leads
    ? (data.captured_leads / data.total_leads) * 100
    : 0;

  return (
    <section className="mb-8" data-testid="customer-intent-analytics">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="qd-overline">Customer Intent</div>
          <div className="text-[11px] text-white/45 mt-1">
            Leads · purchases · conversion rate split by the seller&apos;s stated goal.
            {data && (
              <span className="text-white/30 ml-2">
                · {data.captured_leads} of {data.total_leads} leads have intent captured ({fmtPct(capturePct)})
              </span>
            )}
          </div>
        </div>
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E] overflow-x-auto max-w-full" role="tablist" data-testid="ci-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.v}
              data-testid={`ci-range-${r.v}`}
              onClick={() => setRange(r.v)}
              className={`px-2.5 sm:px-3 py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors whitespace-nowrap ${
                range === r.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading customer intent…
        </div>
      ) : (
        <div className="qd-card p-0 overflow-hidden" data-testid="ci-table-card">
          <div className="px-4 sm:px-5 py-4 border-b border-[#1A1A1A] flex items-baseline justify-between">
            <div>
              <div className="qd-overline">Leads by Intent</div>
              <div className="text-[11px] text-white/45 mt-1">7 canonical intents · stable ordering</div>
            </div>
            {data?.unknown_leads > 0 && (
              <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/35" data-testid="ci-unknown-pill">
                {data.unknown_leads} legacy (no intent)
              </div>
            )}
          </div>

          <table className="w-full text-xs font-mono-data" data-testid="ci-table">
            <thead>
              <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                <th className="px-3 py-2 text-left">Goal</th>
                <th className="px-3 py-2 text-right">Leads</th>
                <th className="px-3 py-2 text-right">Share</th>
                <th className="px-3 py-2 text-right">Purchases</th>
                <th className="px-3 py-2 text-right">Conv %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.intent}
                  className="border-b border-[#141414] hover:bg-white/[0.02]"
                  data-testid={`ci-row-${r.intent}`}
                >
                  <td className="px-3 py-2.5 text-white font-display normal-case leading-tight">
                    {r.label}
                  </td>
                  <td className="px-3 py-2.5 text-right text-white">{r.count}</td>
                  <td className="px-3 py-2.5 text-right text-white/80">{fmtPct(r.pct)}</td>
                  <td className="px-3 py-2.5 text-right text-white">{r.won_count}</td>
                  <td className={`px-3 py-2.5 text-right ${r.conversion_rate > 0 ? "text-emerald-400" : "text-white/40"}`}>
                    {fmtPct(r.conversion_rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
